import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  getCountyOptionsByCountry,
  leadDeliveryDetailsFormData,
  leadDetailsFormData,
} from "./leadAllFields";
import FormField from "../../../components/common/FormField";
import {
  getAllLinkedItemsAction,
  getAllRoomListAction,
} from "../../../store/reducers/leadSurveySlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { ExpandMore } from "@mui/icons-material";
import { formatVolumeUnit } from "../../../hooks/helper";

interface LeadField {
  id: string;
  label: string;
  placeHolder: string;
  type: string;
  options?: string[];
  md: number;
  inputProps?: {
    maxLength?: number;
  };
  patternAllowed?: RegExp;
}

interface LeadDetails {
  generated_id: string;
  referrer_id: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  follow_up_date: string | null;
  moving_on_date: string | null;
  packing_on_date: string | null;
  collection_building_number: string;
  collection_street: string;
  collection_town: string;
  collection_county: string;
  collection_postcode: string;
  collection_country: string;
  collection_purchase_status: string;
  collection_house_size: string;
  collection_distance: string;
  collection_volume: string;
  collection_volume_unit: string;
  delivery_building_number: string;
  delivery_street: string;
  delivery_town: string;
  delivery_county: string;
  delivery_postcode: string;
  delivery_country: string;
  delivery_purchase_status: string;
  delivery_house_size: string;
  delivery_distance: string;
  delivery_volume: string;
  delivery_volume_unit: string;
  customer_notes: string;
  returning_distance?: string;
}

interface LeadFormDetailsProps {
  leadDetails: {
    lead: LeadDetails;
  } | null;
}

const LeadFormDetails: React.FC<LeadFormDetailsProps> = ({
  leadDetails,
}: any) => {
  const collectionAddresses = leadDetails?.lead?.collection_address || [
    {
      building_number: "",
      street: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      purchaseStatus: "",
      houseSize: "",
      distance: 0,
      volume: 0,
      volumeUnit: "m3",
    },
  ];
  const deliveryAddresses = leadDetails?.lead?.delivery_address || [
    {
      building_number: "",
      street: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      purchaseStatus: "",
      houseSize: "",
      distance: 0,
      volume: 0,
      volumeUnit: "m3",
    },
  ];

  const initialFormDetails: LeadDetails = {
    generated_id: "",
    referrer_id: "",
    follow_up_date: "",
    moving_on_date: null,
    packing_on_date: null,
    collection_purchase_status: "",
    collection_house_size: "",
    collection_distance: "",
    collection_volume: "",
    collection_volume_unit: "",
    delivery_purchase_status: "",
    delivery_house_size: "",
    delivery_distance: "",
    delivery_volume: "",
    delivery_volume_unit: "",
    customer_notes: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    collection_building_number: "",
    collection_street: "",
    collection_town: "",
    collection_county: "",
    collection_postcode: "",
    collection_country: "",
    delivery_building_number: "",
    delivery_street: "",
    delivery_town: "",
    delivery_county: "",
    delivery_postcode: "",
    delivery_country: "",
  };

  const [leadFormDetails, setLeadFormDetails] =
    useState<LeadDetails>(initialFormDetails);

  const [totalDistance, setTotalDistance] = useState(0);

  useEffect(() => {
    if (leadFormDetails) {
      // Sum collection distances
      const collectionDistanceSum = collectionAddresses.reduce(
        (acc: number, addr: any) => acc + Number(addr.distance || 0),
        0
      );

      // Sum delivery distances
      const deliveryDistanceSum = deliveryAddresses.reduce(
        (acc: number, addr: any) => acc + Number(addr.distance || 0),
        0
      );

      // Returning distance
      const returningDistance = Number(leadFormDetails.returning_distance || 0);

      // Total distance
      setTotalDistance(
        collectionDistanceSum + deliveryDistanceSum + returningDistance
      );
    }
  }, [
    collectionAddresses,
    deliveryAddresses,
    leadFormDetails?.returning_distance,
  ]);

  const { surveyRoomList, linkedItems } = useSelector(
    (state: any) => state.leadSurvey
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (!surveyRoomList) {
      dispatch(getAllRoomListAction(leadDetails?.lead?.generated_id));
    }
    if (!linkedItems) {
      dispatch(getAllLinkedItemsAction());
    }
  }, [leadDetails?.lead?.generated_id]);

  useEffect(() => {
    if (leadDetails) {
      setLeadFormDetails(leadDetails.lead);
    } else {
      setLeadFormDetails(initialFormDetails);
    }
  }, [leadDetails]);

  // Set county dropdown options based on country
  const getCountyOptions = (country: string) => {
    return getCountyOptionsByCountry(country);
  };

  const formatDate = (date: string | null) => {
    return date ? dayjs(date).format("HH:mm - DD/MM/YYYY") : "";
  };

  const [collectionTab, setCollectionTab] = useState(0);
  const [deliveryTab, setDeliveryTab] = useState(0);

const getCumulativeDistance = (tabIndex: number) => {
  let sum = 0;

  for (let i = 0; i <= tabIndex; i++) {
    sum += Number(collectionAddresses?.[i]?.distance || 0);
  }

  return sum;
};

  return (
    <>
      <Accordion
        defaultExpanded
        sx={{
          width: "100%",
          padding: "0px",
          border: "none",
          boxShadow: "none",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            padding: "0px 10px",
            border: "none",
            minHeight: "0px !important",
            "& .MuiAccordionSummary-content": {
              margin: "0px !important", // default
            },
            "&.Mui-expanded .MuiAccordionSummary-content": {
              margin: "0px !important", // when expanded
            },
          }}
        >
          <Typography
            align="left"
            sx={{
              textAlign: "left",
              padding: "0px",
              fontWeight: 800,
              color: "#333333",
              fontSize: "16px",
            }}
            variant="subtitle1"
          >
            Customer Notes
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: "0px 6px" }}>
          <Grid container spacing={2} sx={{ width: "100% !important" }}>
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                padding: "0px !important",
                paddingTop: "0px !important",
                borderBottom: "1px solid #C4CDD5",
              }}
            >
              <Typography
                align="left"
                sx={{
                  textAlign: "left",
                  padding: "0px",
                  fontWeight: 500,
                  color: "#444444",
                  "&::first-letter": {
                    textTransform: "uppercase",
                  },
                }}
                variant="subtitle1"
              >
                {leadFormDetails.customer_notes}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            sx={{
              width: "100% !important",
              marginTop: "10px",
              padding: "0px !important",
              border: "1px solid #C4CDD5",
              borderRadius: "5px",
              overflow: "hidden",
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                backgroundColor: "#e5f4f1",
                padding: "4px !important",
                marginBottom: "10px",
              }}
            >
              {/* Tabs for Collection Addresses */}
              <Tabs
                value={collectionTab}
                onChange={(e, val) => setCollectionTab(val)}
                aria-label="Collection Address Tabs"
                TabIndicatorProps={{ style: { display: "none" } }} // Hide the tab indicator line
                sx={{
                  minHeight: "20px",
                  "& .MuiTabs-flexContainer": {
                    gap: 1, // adds space between tabs (theme.spacing(1))
                  },
                  padding: "4px 6px",
                }}
              >
                {collectionAddresses?.map((address: any, index: number) => (
                  <Tab
                    sx={{
                      textAlign: "left",
                      padding: "6px 4px",
                      fontWeight: 600,
                      color: "#333333",
                      textTransform: "capitalize",
                      background:
                        collectionTab === index ? "#fff" : "transparent",
                      borderRadius: "5px",
                      minHeight: "32px",
                    }}
                    key={index}
                    label={`Collection ${index + 1}`}
                  />
                ))}
              </Tabs>
            </Grid>

            {/* Tab Panel for each collection */}
            {collectionAddresses.map(
              (collection: any, index: number) =>
                collectionTab === index && (
                  <React.Fragment key={index}>
                    {leadDetailsFormData.map((field: LeadField, i: number) => {
                      let value =
                        collection[field.id as keyof LeadDetails] || "";
                      if (
                        field.id === "follow_up_date" ||
                        field.id === "moving_on_date" ||
                        field.id === "packing_on_date"
                      ) {
                        value = formatDate(value as string);
                      }
                      if (field?.id === "volume") {
                        value = !value ? 0 : value?.toFixed(2);
                      }else if(field?.id ==='distance'){
                        const cumulativeDistance = getCumulativeDistance(index);
                        value = cumulativeDistance.toFixed(2);
                      }
                      if (field?.id === "volume_unit") {
                          value = formatVolumeUnit(value);
                        }
                      // Add "Delivery" title when delivery fields start
                      const isDeliveryStart = field.id === "delivery_street"; // Replace with the actual start field ID

                      return (
                        <>
                          {isDeliveryStart && (
                            <Grid
                              item
                              xs={12}
                              sm={12}
                              style={{ paddingLeft: "0px" }}
                            >
                              <Typography
                                align="left"
                                sx={{
                                  textAlign: "left",
                                  padding: "0px",
                                  marginTop: "-10px",
                                  fontWeight: 600,
                                  color: "#333333",
                                }}
                                variant="subtitle1"
                              >
                                Delivery
                              </Typography>
                            </Grid>
                          )}
                          <FormField
                            key={`${field.id}-${index}`}
                            onPaste={(
                              e: React.ClipboardEvent<HTMLInputElement>
                            ) => e.preventDefault()}
                            {...{
                              ...field,
                              value ,
                              options: field.id === "county"
                                  ? getCountyOptions(
                                      leadFormDetails?.delivery_county ||
                                        leadFormDetails?.collection_county
                                    )
                                  : field.options, // Conditional options for county based on country
                              disabled: true,

                              variant: "outlined", // Adding this variant prop
                              isNonMarginTop: true,
                              InputProps: {
                                sx:
                                  field.type === "text" ||
                                  field.type === "password"
                                    ? { height: "36px" }
                                    : {
                                        padding: "6px 12px",
                                        // height: "36px",
                                      },
                              },
                            }}
                          />
                        </>
                      );
                    })}
                  </React.Fragment>
                )
            )}

            {/* Tabs for Delivery Addresses */}
          </Grid>
          <Grid
            container
            sx={{
              width: "100% !important",
              padding: "0px !important",
              marginTop: "10px",
              border: "1px solid #C4CDD5",
              borderRadius: "5px",
              overflow: "hidden",
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                backgroundColor: "#e5f4f1",
                padding: "0px !important",
                marginBottom: "10px",
              }}
            >
              {/* Tabs for Collection Addresses */}
              <Tabs
                value={deliveryTab}
                onChange={(e, val) => setDeliveryTab(val)}
                aria-label="Collection Address Tabs"
                TabIndicatorProps={{ style: { display: "none" } }} // Hide the tab indicator line
                sx={{
                  minHeight: "20px",
                  "& .MuiTabs-flexContainer": {
                    gap: 1, // adds space between tabs (theme.spacing(1))
                  },
                  padding: "4px 6px",
                }}
              >
                {deliveryAddresses?.map((address: any, index: number) => (
                  <Tab
                    sx={{
                      textAlign: "left",
                      padding: "6px 4px",
                      fontWeight: 600,
                      color: "#333333",
                      textTransform: "capitalize",
                      background:
                        deliveryTab === index ? "#fff" : "transparent",
                      borderRadius: "5px",
                      minHeight: "32px",
                    }}
                    key={index}
                    label={`Delivery ${index + 1}`}
                  />
                ))}
              </Tabs>
            </Grid>

            {/* Tab Panel for each collection */}
            {deliveryAddresses.map(
              (collection: any, index: number) =>
                deliveryTab === index && (
                  <React.Fragment key={index}>
                    {leadDeliveryDetailsFormData.map(
                      (field: LeadField, i: number) => {
                        let value =
                          field.id === "returning_distance"
                            ? totalDistance?.toFixed(2)
                            : collection[field.id as keyof LeadDetails] || "";
                        if (
                          field.id === "follow_up_date" ||
                          field.id === "moving_on_date" ||
                          field.id === "packing_on_date"
                        ) {
                          value = formatDate(value as string);
                        }
                        // Add "Delivery" title when delivery fields start
                        const isDeliveryStart = field.id === "delivery_street"; // Replace with the actual start field ID

                        return (
                          <>
                            {isDeliveryStart && (
                              <Grid
                                item
                                xs={12}
                                sm={12}
                                style={{ paddingLeft: "0px" }}
                              >
                                <Typography
                                  align="left"
                                  sx={{
                                    textAlign: "left",
                                    padding: "0px",
                                    marginTop: "-10px",
                                    fontWeight: 600,
                                    color: "#333333",
                                  }}
                                  variant="subtitle1"
                                >
                                  Delivery
                                </Typography>
                              </Grid>
                            )}
                            <FormField
                              key={`${field.id}-${index}`}
                              onPaste={(
                                e: React.ClipboardEvent<HTMLInputElement>
                              ) => e.preventDefault()}
                              {...{
                                ...field,
                                value,
                                options:
                                  field.id === "county"
                                    ? getCountyOptions(
                                        leadFormDetails?.delivery_county ||
                                          leadFormDetails?.collection_county
                                      )
                                    : field.options, // Conditional options for county based on country
                                disabled: true,

                                variant: "outlined", // Adding this variant prop
                                isNonMarginTop: true,
                                InputProps: {
                                  sx:
                                    field.type === "text" ||
                                    field.type === "password"
                                      ? { height: "36px" }
                                      : {
                                          padding: "6px 12px",
                                        },
                                },
                              }}
                            />
                          </>
                        );
                      }
                    )}
                  </React.Fragment>
                )
            )}

            {/* Tabs for Delivery Addresses */}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default LeadFormDetails;
